/**
 * The control a nonprofit demo teacher uses to see the product as their linked
 * sample student, and to come back.
 *
 * Renders nothing at all unless the server said this account may switch, so it
 * is safe to mount unconditionally on the shared account surfaces (desktop
 * header and mobile drawer). While the sample-student view is on, the account
 * chrome around it already describes the student — this control's job is to say
 * plainly that the view is borrowed and to offer the way out.
 *
 * The status chip uses the amber-wash treatment (DESIGN.md §4/§15): amber is
 * the surface, the words stay ink, because "you are not looking at your own
 * account" is a state nobody may misread.
 *
 * Update when the switch endpoints, their copy, or the surfaces that host this
 * control change.
 */

import { type FC, useCallback, useState } from 'react';
import { alpha, Box, Button, Chip, Typography } from '@mui/material';

import {
  describeDemoViewError,
  enterDemoStudentView,
  exitDemoStudentView,
} from '@/auth/demoView';
import { useAuth } from '@/hooks/useAuth';

interface DemoViewSwitchProps {
  /**
   * `header` lays the chip, button and any error out in a row beside the rest
   * of the account summary; `drawer` stacks and centres them under it, and
   * owns the gap to whatever it sits below so a hidden control leaves no hole.
   */
  layout?: 'header' | 'drawer';
  size?: 'small' | 'medium';
}

type SwitchAction = (getAccessToken: () => Promise<string>) => Promise<void>;

const DemoViewSwitch: FC<DemoViewSwitchProps> = ({
  layout = 'header',
  size = 'medium',
}) => {
  const { demoView, getAccessToken } = useAuth();
  const [isSwitching, setIsSwitching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const runSwitch = useCallback(
    async (action: SwitchAction) => {
      setErrorMessage(null);
      setIsSwitching(true);
      try {
        await action(getAccessToken);
        // A successful switch navigates out of this document, so the control
        // deliberately stays busy until it does — a second click would fire a
        // second switch against a page that is already leaving.
      } catch (err) {
        setErrorMessage(describeDemoViewError(err));
        setIsSwitching(false);
      }
    },
    [getAccessToken]
  );

  if (!demoView?.available) return null;

  const isActive = demoView.active;
  const isDrawer = layout === 'drawer';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isDrawer ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        mt: isDrawer ? 1.25 : 0,
      }}
    >
      {isActive && (
        <Chip
          role="status"
          size="small"
          label="Demo: student view"
          sx={theme => ({
            height: 22,
            borderRadius: theme.custom.radii.pill,
            fontWeight: 800,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: '0.66rem',
            color: 'text.primary',
            bgcolor: alpha(theme.palette.warning.dark, 0.16),
            border: `1px solid ${alpha(theme.palette.warning.dark, 0.55)}`,
            '& .MuiChip-label': { px: 1 },
          })}
        />
      )}
      <Button
        variant="outlined"
        color="primary"
        size={size}
        disabled={isSwitching}
        onClick={() => {
          void runSwitch(isActive ? exitDemoStudentView : enterDemoStudentView);
        }}
        sx={theme => ({
          borderRadius: theme.custom.radii.pill,
          whiteSpace: 'nowrap',
        })}
      >
        {isActive ? 'Back to teacher view' : 'View as student'}
      </Button>
      {errorMessage && (
        <Typography
          role="alert"
          variant="caption"
          sx={{ color: 'error.main', textAlign: isDrawer ? 'center' : 'start' }}
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default DemoViewSwitch;
